import QueryString from 'qs'
import { ACCESS_KEY, urlAPI } from '../../../../urls'

type AnyObj = Record<string, any>

// drop_price email is ONLY for users with real active advertises on this unit
// WaitingList is queue metadata only (reserve/notify) and is intentionally excluded here.
const DROP_PRICE_TERMINAL_AD_STATUSES = new Set([
    'completed',
    'canceled',
    'cancelled',
    'ads removed',
    'advertisement closed',
    'ads removing rejected',
])

const toLower = (value: unknown): string => String(value ?? '').trim().toLowerCase()
const toArray = <T,>(value: T[] | null | undefined): T[] => (Array.isArray(value) ? value : [])

const getAdvertiseStatuses = (advertise: AnyObj): string[] => {
    const statusesRaw = advertise?.attributes?.Statuses ?? advertise?.Statuses ?? []
    return toArray<string>(statusesRaw).map(toLower).filter(Boolean)
}

const parseDate = (value: unknown): Date | null => {
    if (value == null || value === '') return null
    const parsed = new Date(String(value))
    return Number.isNaN(parsed.getTime()) ? null : parsed
}

const isAdvertiseActiveByInterval = (advertise: AnyObj): boolean => {
    const startDateRaw = advertise?.attributes?.StartDate ?? advertise?.StartDate
    const endDateRaw = advertise?.attributes?.EndDate ?? advertise?.EndDate
    const startDate = parseDate(startDateRaw)
    const endDate = parseDate(endDateRaw)
    const hasAnyDate = Boolean(startDateRaw) || Boolean(endDateRaw)

    // Agent-side rule: if no dates => treat as active.
    if (!hasAnyDate) return true
    // If date fields exist but are malformed, fail-safe to exclude from compliance email.
    if ((startDateRaw && startDate == null) || (endDateRaw && endDate == null)) return false

    const now = Date.now()
    if (startDate && now < startDate.getTime()) return false
    if (endDate && now > endDate.getTime()) return false
    return true
}

export const isActiveAdvertiseForDropPrice = (advertise: AnyObj): boolean => {
    const statuses = getAdvertiseStatuses(advertise)
    if (statuses.some((status) => DROP_PRICE_TERMINAL_AD_STATUSES.has(status))) return false
    if (!isAdvertiseActiveByInterval(advertise)) return false
    return true
}

export type DropPriceRecipientsResult = {
    userIds: number[]
    totalAds: number
    activeAds: number
}

export const fetchDropPriceRecipients = async (unitId: number): Promise<DropPriceRecipientsResult> => {
    const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
    const queryObject = {
        filters: {
            Unit: {
                id: { $eq: unitId }
            }
        },
        fields: ['Statuses', 'StartDate', 'EndDate', 'updatedAt'],
        populate: {
            User: {
                fields: ['id']
            },
            Unit: {
                fields: ['id']
            }
        }
    }
    const body = {
        method: 'GET',
        collection: 'unit-advertises',
        query: QueryString.stringify(queryObject, { encode: false })
    }

    const response = await fetch(urlAPI, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`
        },
        body: JSON.stringify(body)
    })
    if (!response.ok) throw new Error(`Failed to fetch unit advertises (${response.status})`)

    const json = await response.json()
    const list = Array.isArray(json?.response?.data) ? json.response.data : []
    const active = list.filter(isActiveAdvertiseForDropPrice)
    const ids: number[] = Array.from(new Set<number>(
        active
            .map((item: AnyObj) => Number(item?.attributes?.User?.data?.id ?? item?.User?.id ?? item?.User))
            .filter((id: number): id is number => Number.isFinite(id) && id > 0)
    ))

    return {
        userIds: ids,
        totalAds: list.length,
        activeAds: active.length
    }
}
